<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';

const route = useRoute();
const { accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const administratorTabs = [
  {
    label: 'Общие',
    icon: 'i-lucide-sliders-horizontal',
    routeName: 'general_settings_index',
    activeOn: ['general_settings_index'],
  },
  {
    label: 'Операторы',
    icon: 'i-lucide-square-user',
    routeName: 'agent_list',
    activeOn: ['agent_list'],
  },
  {
    label: 'Команды',
    icon: 'i-lucide-users',
    routeName: 'settings_teams_list',
    activeOn: [
      'settings_teams_list',
      'settings_teams_new',
      'settings_teams_finish',
      'settings_teams_add_agents',
      'settings_teams_edit',
      'settings_teams_edit_members',
      'settings_teams_edit_finish',
    ],
  },
  {
    label: 'Каналы',
    icon: 'i-lucide-inbox',
    routeName: 'settings_inbox_list',
    activeOn: [
      'settings_inbox_list',
      'settings_inbox_show',
      'settings_inbox_new',
      'settings_inbox_finish',
      'settings_inboxes_page_channel',
      'settings_inboxes_add_agents',
    ],
  },
  {
    label: 'Метки',
    icon: 'i-lucide-tags',
    routeName: 'labels_list',
    activeOn: ['labels_list'],
  },
  {
    label: 'Поля',
    icon: 'i-lucide-list-tree',
    routeName: 'attributes_list',
    activeOn: ['attributes_list'],
  },
  {
    label: 'Автоматизация',
    icon: 'i-lucide-workflow',
    routeName: 'automation_list',
    activeOn: ['automation_list'],
  },
  {
    label: 'Боты',
    icon: 'i-lucide-bot',
    routeName: 'agent_bots',
    activeOn: ['agent_bots'],
  },
  {
    label: 'Макросы',
    icon: 'i-lucide-toy-brick',
    routeName: 'macros_wrapper',
    activeOn: ['macros_wrapper', 'macros_new', 'macros_edit'],
  },
  {
    label: 'Шаблоны',
    icon: 'i-lucide-message-square-quote',
    routeName: 'canned_list',
    activeOn: ['canned_list'],
  },
  {
    label: 'Интеграции',
    icon: 'i-lucide-blocks',
    routeName: 'settings_applications',
    activeOn: [
      'settings_applications',
      'settings_applications_integration',
      'settings_integrations_dashboard_apps',
      'settings_integrations_webhook',
      'settings_integrations_slack',
      'settings_integrations_linear',
      'settings_integrations_notion',
      'settings_integrations_shopify',
    ],
  },
  {
    label: 'Роли',
    icon: 'i-lucide-shield-check',
    routeName: 'custom_roles_list',
    activeOn: ['custom_roles_list'],
  },
];

const memberTabs = administratorTabs.filter(tab =>
  ['Макросы', 'Шаблоны'].includes(tab.label)
);

const tabs = computed(() => (isAdmin.value ? administratorTabs : memberTabs));

const isTabActive = tab => tab.activeOn.includes(route.name);
</script>

<template>
  <section
    class="medup-settings-tabs w-full min-w-0 px-3 pt-5 sm:px-6"
    aria-label="Разделы настроек"
  >
    <header
      class="medup-settings-tabs__heading mb-4 border-b border-[#27283e]/[0.12] pb-4 dark:border-n-weak"
    >
      <span
        class="mb-1.5 flex items-center gap-[7px] text-[10px] font-extrabold leading-none tracking-[0.1em] uppercase text-[#5929e2] dark:text-n-iris-11"
      >
        <span
          class="h-0.5 w-[18px] rounded-full bg-current"
          aria-hidden="true"
        />
        MedUp Забота
      </span>
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1
          class="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.6px] text-[#27283e] dark:text-n-slate-12"
        >
          Настройки
        </h1>
        <p
          class="m-0 text-xs leading-relaxed text-[#474852] dark:text-n-slate-11"
        >
          Управляйте командой, каналами и правилами рабочего пространства.
        </p>
      </div>
    </header>

    <nav
      class="medup-settings-tabs__scroll w-full overflow-x-auto overscroll-x-contain rounded-xl border border-[#27283e]/[0.12] bg-white/[0.92] p-1 dark:border-n-weak dark:bg-n-surface-1"
      aria-label="Навигация по настройкам"
    >
      <div
        class="medup-settings-tabs__control flex w-max min-w-full gap-1 sm:w-auto sm:flex-wrap"
      >
        <router-link
          v-for="tab in tabs"
          :key="tab.routeName"
          :to="accountScopedRoute(tab.routeName)"
          class="medup-settings-tab inline-flex min-h-9 shrink-0 items-center gap-[7px] rounded-lg px-3 py-2 text-xs font-medium leading-none no-underline transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#5929e2] motion-reduce:transition-none"
          :class="
            isTabActive(tab)
              ? 'medup-settings-tab--active bg-[#5929e2]/[0.08] text-[#5929e2] hover:bg-[#5929e2]/[0.12] dark:bg-n-iris-3 dark:text-n-iris-11'
              : 'text-[#474852] hover:bg-[#f4f4f4] hover:text-[#27283e] dark:text-n-slate-11 dark:hover:bg-n-surface-2 dark:hover:text-n-slate-12'
          "
          :aria-current="isTabActive(tab) ? 'page' : undefined"
        >
          <span :class="tab.icon" class="size-4 shrink-0" aria-hidden="true" />
          <span>{{ tab.label }}</span>
        </router-link>
      </div>
    </nav>
  </section>
</template>
